import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import VictoryModal from '../../components/game/VictoryModal';
import HowToPlayModal from '../../components/game/HowToPlayModal';
import { useAuthStore } from '../../store/authStore';
import api from '../../services/api';
import { useGameProgress } from '../../hooks/useGameProgress';
import { useSessionContext } from '../../hooks/useSessionContext';
import SessionBanner from '../../components/game/SessionBanner';
import { useAccessibility } from '../../contexts/AccessibilityContext';

// ======== Tipos ========

type SymbolValue = 0 | 1 | 2 | 3;

interface FritoVariadoPuzzle {
  seed: number;
  width: number;
  height: number;
  regions: number[];
  puzzle: SymbolValue[];
  solution: SymbolValue[];
}

// ======== Constantes ========

const DIFFICULTIES = [
  { id: 1, nombre: 'Neurona', icono: '🧠', color: '#22c55e', width: 3, height: 4 },
  { id: 2, nombre: 'Sinapsis', icono: '⚡', color: '#3b82f6', width: 6, height: 6 },
  { id: 3, nombre: 'Cerebro', icono: '🧩', color: '#a855f7', width: 6, height: 9 },
  { id: 4, nombre: 'Genio', icono: '💡', color: '#ef4444', width: 9, height: 9 },
  { id: 5, nombre: 'Einstein', icono: '🎓', color: '#f97316', width: 12, height: 12 },
];

// 6 paleta de colores para las regiones (igual que el prototipo)
const REGION_PALETTE = ['#E8F1FF', '#EAF7E9', '#FFF7DA', '#F2E9FF', '#FFEAE5', '#E7F7F1'];

// Paleta alto contraste: colores -200 saturados, legibles sobre fondo oscuro
const HC_REGION_PALETTE = ['#bfdbfe', '#bbf7d0', '#fef08a', '#fbcfe8', '#c7d2fe', '#fed7aa'];

// Imágenes de los fritos (públicas en /games/frito_variado/)
const FOOD_IMAGES: Record<1 | 2 | 3, string> = {
  1: '/games/frito_variado/calamares.png',
  2: '/games/frito_variado/boquerones.png',
  3: '/games/frito_variado/gambas.png',
};

const FOOD_LABELS: Record<SymbolValue, string> = {
  0: 'Vacío',
  1: 'Calamares',
  2: 'Boquerones',
  3: 'Gambas',
};

function nextSymbol(v: SymbolValue): SymbolValue {
  return ((v + 1) % 4) as SymbolValue;
}

// ======== Componente Celda ========

interface CellProps {
  value: SymbolValue;
  fixed: boolean;
  highlighted: boolean;
  regionColor: string;
  regionIndex: number;
  borderTop: boolean;
  borderRight: boolean;
  borderBottom: boolean;
  borderLeft: boolean;
  hasError?: boolean;
  onClick: () => void;
}

function FritoCell({
  value, fixed, highlighted,
  regionColor, regionIndex, borderTop, borderRight, borderBottom, borderLeft,
  hasError = false,
  onClick,
}: CellProps) {
  const { highContrast } = useAccessibility();
  const image = value !== 0 ? FOOD_IMAGES[value as 1 | 2 | 3] : null;

  const borderWidth = (side: boolean) => side ? (highlighted ? 4 : 3) : 1;
  const borderColor = (side: boolean) => side ? '#1f2937' : (highContrast ? '#ffffff' : '#cbd5e1');

  const bgColor = hasError
    ? (highContrast ? '#ff4444' : '#fca5a5')
    : (highContrast ? HC_REGION_PALETTE[regionIndex % HC_REGION_PALETTE.length] : regionColor);

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={fixed}
      aria-label={FOOD_LABELS[value]}
      title={FOOD_LABELS[value]}
      style={{
        width: '100%',
        height: '100%',
        backgroundColor: bgColor,
        borderTop: `${borderWidth(borderTop)}px solid ${borderColor(borderTop)}`,
        borderRight: `${borderWidth(borderRight)}px solid ${borderColor(borderRight)}`,
        borderBottom: `${borderWidth(borderBottom)}px solid ${borderColor(borderBottom)}`,
        borderLeft: `${borderWidth(borderLeft)}px solid ${borderColor(borderLeft)}`,
        boxShadow: highlighted
          ? 'inset 0 0 0 2px rgba(14,165,233,0.5)'
          : fixed
          ? 'inset 0 0 0 3px #b45309'
          : undefined,
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: fixed ? 'default' : 'pointer',
        transition: 'filter 0.1s',
      }}
      onMouseEnter={e => { if (!fixed) (e.currentTarget as HTMLElement).style.filter = 'brightness(0.94)'; }}
      onMouseLeave={e => { (e.currentTarget as HTMLElement).style.filter = ''; }}
    >
      {/* Triángulo esquina para celdas fijas */}
      {fixed && (
        <span
          aria-hidden="true"
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 0,
            height: 0,
            borderTop: '14px solid rgba(15,23,42,0.22)',
            borderLeft: '14px solid transparent',
          }}
        />
      )}

      {image ? (
        <div
          style={{
            height: '76%',
            aspectRatio: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            backgroundColor: fixed ? 'rgba(255,255,255,0.88)' : 'rgba(255,255,255,0.18)',
            boxShadow: fixed
              ? '0 0 0 2px rgba(15,23,42,0.18), 0 4px 8px rgba(15,23,42,0.08)'
              : 'inset 0 0 0 1px rgba(15,23,42,0.06)',
          }}
        >
          <img
            src={image}
            alt={FOOD_LABELS[value]}
            draggable={false}
            style={{
              width: '70%',
              height: '70%',
              objectFit: 'contain',
              opacity: fixed ? 1 : 0.9,
              filter: fixed
                ? 'drop-shadow(0 2px 2px rgba(15,23,42,0.18)) saturate(1.05)'
                : 'saturate(0.88) contrast(0.96)',
            }}
          />
        </div>
      ) : (
        <span style={{ color: highContrast ? '#ffffff' : '#94a3b8', fontSize: 'clamp(10px, 3vw, 40px)', userSelect: 'none' }}>+</span>
      )}
    </button>
  );
}


// ======== Componente principal ========

export default function FritoVariadoGame() {
  const { dificultad: dificultadParam } = useParams<{ dificultad: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuthStore();

  const difficulty = parseInt(dificultadParam || '1');
  const currentDiff = DIFFICULTIES.find(d => d.id === difficulty) || DIFFICULTIES[0];
  const seedParam = searchParams.get('seed') ? parseInt(searchParams.get('seed')!) : undefined;

  const { saveProgress: saveGameProgress } = useGameProgress('frito_variado', difficulty);
  const { isInSession, sessionType, saveSessionResult } = useSessionContext();

  // Estado del juego
  const [puzzle, setPuzzle] = useState<FritoVariadoPuzzle | null>(null);
  const [values, setValues] = useState<SymbolValue[]>([]);
  const [fixed, setFixed] = useState<boolean[]>([]);
  const [selectedCell, setSelectedCell] = useState<number | null>(null);

  // Historial undo/redo (índice-based, igual que Hitori)
  const [history, setHistory] = useState<SymbolValue[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // UI
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);

  // Medición del tablero
  const boardAreaRef = useRef<HTMLDivElement>(null);
  const [boardW, setBoardW] = useState(0);
  const [boardH, setBoardH] = useState(0);

  const measureBoard = () => {
    if (boardAreaRef.current) {
      const { clientWidth, clientHeight } = boardAreaRef.current;
      setBoardW(clientWidth - 8);
      setBoardH(clientHeight - 8);
    }
  };

  useEffect(() => {
    if (!loading) requestAnimationFrame(measureBoard);
  }, [loading]);

  useEffect(() => {
    window.addEventListener('resize', measureBoard);
    return () => window.removeEventListener('resize', measureBoard);
  }, []);
  const [startTime] = useState(Date.now());
  const [elapsedTime, setElapsedTime] = useState(0);
  const [moves, setMoves] = useState(0);
  const [solved, setSolved] = useState(false);
  const [victoryModalOpen, setVictoryModalOpen] = useState(false);
  const [showHowToPlay, setShowHowToPlay] = useState(false);
  const [modoAsistido, setModoAsistido] = useState(false);

  // Timer
  useEffect(() => {
    if (solved) return;
    const timer = setInterval(() => {
      setElapsedTime(Math.floor((Date.now() - startTime) / 1000));
    }, 1000);
    return () => clearInterval(timer);
  }, [solved, startTime]);

  // Carga al cambiar dificultad
  useEffect(() => {
    generateNewPuzzle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [difficulty]);

  async function generateNewPuzzle(customSeed?: number, forceNew = false) {
    try {
      setLoading(true);
      setLoadingProgress(20);
      setSelectedCell(null);

      const params: Record<string, unknown> = { dificultad: difficulty };
      if (!forceNew) {
        if (customSeed !== undefined) {
          params.seed = customSeed;
        } else if (seedParam !== undefined) {
          params.seed = seedParam;
        }
      }

      const response = await api.get('/frito-variado/generate', { params });

      setLoadingProgress(70);

      const data = response.data.data as FritoVariadoPuzzle;
      setPuzzle(data);
      initializeGame(data);

      // Actualizar URL con seed
      const newParams = new URLSearchParams(searchParams);
      newParams.set('seed', data.seed.toString());
      setSearchParams(newParams, { replace: true });

      setLoadingProgress(100);
      setTimeout(() => setLoading(false), 250);
    } catch (error) {
      console.error('Error generando Frito Variado:', error);
      toast.error('Error al generar puzzle');
      setLoading(false);
    }
  }

  function initializeGame(data: FritoVariadoPuzzle) {
    const initialValues = [...data.puzzle] as SymbolValue[];
    const fixedCells = data.puzzle.map(v => v !== 0);
    setValues(initialValues);
    setFixed(fixedCells);
    setHistory([[...initialValues]]);
    setHistoryIndex(0);
    setMoves(0);
    setSolved(false);
  }

  // ======== Acciones del tablero ========

  function saveToHistory(newValues: SymbolValue[]) {
    setHistory(prev => {
      const truncated = prev.slice(0, historyIndex + 1);
      const newHist = [...truncated, [...newValues]];
      // Max 50 entradas
      if (newHist.length > 50) newHist.shift();
      setHistoryIndex(newHist.length - 1);
      return newHist;
    });
  }

  function handleCellClick(cellIndex: number) {
    if (solved || !puzzle) return;
    setSelectedCell(cellIndex);
    if (fixed[cellIndex]) return;

    const newValues = [...values] as SymbolValue[];
    newValues[cellIndex] = nextSymbol(newValues[cellIndex]);
    setValues(newValues);
    setMoves(m => m + 1);
    saveToHistory(newValues);
    checkVictory(newValues, puzzle);
  }

  function undo() {
    if (historyIndex <= 0) return;
    const newIndex = historyIndex - 1;
    setHistoryIndex(newIndex);
    setValues([...history[newIndex]] as SymbolValue[]);
  }

  function redo() {
    if (historyIndex >= history.length - 1) return;
    const newIndex = historyIndex + 1;
    setHistoryIndex(newIndex);
    setValues([...history[newIndex]] as SymbolValue[]);
  }

  function reset() {
    if (!puzzle || !window.confirm('¿Reiniciar el tablero?')) return;
    initializeGame(puzzle);
  }

  // ======== Pista ========

  function giveHint() {
    if (!puzzle || solved) return;

    // 1. Primero corregir errores: celdas con valor incorrecto
    for (let ci = 0; ci < values.length; ci++) {
      if (!fixed[ci] && values[ci] !== 0 && values[ci] !== puzzle.solution[ci]) {
        const newValues = [...values] as SymbolValue[];
        newValues[ci] = puzzle.solution[ci];
        setValues(newValues);
        saveToHistory(newValues);
        toast.error('Corrección: esa celda tenía un valor incorrecto');
        return;
      }
    }

    // 2. Revelar una celda vacía aleatoria
    const candidates: number[] = [];
    for (let ci = 0; ci < values.length; ci++) {
      if (!fixed[ci] && values[ci] === 0) {
        candidates.push(ci);
      }
    }

    if (candidates.length > 0) {
      const pick = candidates[Math.floor(Math.random() * candidates.length)];
      const newValues = [...values] as SymbolValue[];
      newValues[pick] = puzzle.solution[pick];
      setValues(newValues);
      saveToHistory(newValues);
      toast.success(`Pista: ${FOOD_LABELS[puzzle.solution[pick]]}`);
      checkVictory(newValues, puzzle);
    } else {
      toast.info('¡El tablero está completo!');
    }
  }

  // ======== Mostrar solución ========

  function showSolution() {
    if (!puzzle || !window.confirm('¿Ver la solución? No se guardará el progreso.')) return;
    const solutionValues = [...puzzle.solution] as SymbolValue[];
    setValues(solutionValues);
    saveToHistory(solutionValues);
    toast.info('Solución revelada');
  }

  // ======== Victoria ========

  function checkVictory(currentValues: SymbolValue[], currentPuzzle: FritoVariadoPuzzle) {
    if (solved) return;

    const isComplete = currentValues.every(v => v !== 0);
    if (!isComplete) return;

    // Verificar contra la solución (más rápido que revalidar reglas)
    const isSolved = currentValues.every((v, i) => v === currentPuzzle.solution[i]);
    if (!isSolved) return;

    setSolved(true);
    setVictoryModalOpen(true);
    toast.success('¡Frito Variado completado!');

    saveProgressData(currentPuzzle).catch(err => {
      console.error('Error guardando progreso:', err);
    });
  }

  const saveProgressData = useCallback(async (currentPuzzle: FritoVariadoPuzzle) => {
    if (!user) return;
    try {
      await saveGameProgress({
        tablero_id: currentPuzzle.seed,
        tiempo: elapsedTime,
        movimientos: moves,
        completado: true,
      });
      await saveSessionResult({
        tiempo: elapsedTime,
        movimientos: moves,
        completado: true,
        datos_juego: { tablero_id: currentPuzzle.seed, width: currentPuzzle.width, height: currentPuzzle.height },
      });
    } catch (error) {
      console.error('Error guardando progreso:', error);
    }
  }, [user, elapsedTime, moves, saveGameProgress, saveSessionResult]);


  // ======== Modo asistido ========

  function calcularErrores(vals: SymbolValue[], regions: number[], w: number, h: number): Set<number> {
    const errores = new Set<number>();
    const total = w * h;

    // Regla 1: cada región debe ser todo-igual o todo-diferente
    const regionCells = new Map<number, number[]>();
    for (let i = 0; i < total; i++) {
      const rid = regions[i];
      if (!regionCells.has(rid)) regionCells.set(rid, []);
      regionCells.get(rid)!.push(i);
    }
    for (const cells of regionCells.values()) {
      const filled = cells.map(i => vals[i]).filter(v => v !== 0);
      if (filled.length !== cells.length) continue; // región incompleta, no juzgar
      const unique = new Set(filled);
      const allSame = unique.size === 1;
      const allDiff = unique.size === filled.length;
      if (!allSame && !allDiff) cells.forEach(i => errores.add(i));
    }

    // Regla 2: adyacentes de distintas regiones no pueden ser iguales
    for (let row = 0; row < h; row++) {
      for (let col = 0; col < w; col++) {
        const idx = row * w + col;
        const v = vals[idx];
        if (v === 0) continue;
        if (col < w - 1) {
          const r = idx + 1;
          if (regions[r] !== regions[idx] && vals[r] === v) { errores.add(idx); errores.add(r); }
        }
        if (row < h - 1) {
          const b = idx + w;
          if (regions[b] !== regions[idx] && vals[b] === v) { errores.add(idx); errores.add(b); }
        }
      }
    }

    return errores;
  }

  // ======== Render: Loading ========

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-500 via-orange-500 to-red-600 flex items-center justify-center p-4">
        <div className="glass-container max-w-md w-full text-center">
          <div className="mb-6">
            <div className="w-16 h-16 border-4 border-neureka-yellow border-t-transparent rounded-full animate-spin mx-auto mb-4" />
            <h2 className="text-2xl font-bold mb-2">Preparando el frito...</h2>
            <p className="text-white/70">Generando tu tablero Frito Variado</p>
          </div>
          <div className="w-full bg-white/10 rounded-full h-6 overflow-hidden">
            <div
              className="bg-neureka-yellow h-full transition-all duration-300 flex items-center justify-center text-sm font-bold"
              style={{ width: `${loadingProgress}%` }}
            >
              {loadingProgress > 10 && `${loadingProgress}%`}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!puzzle) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-500 via-orange-500 to-red-600 flex items-center justify-center">
        <div className="glass-container max-w-md text-center">
          <p className="text-xl">Error cargando puzzle</p>
          <button onClick={() => navigate('/juegos')} className="btn-neureka mt-4">
            Volver a juegos
          </button>
        </div>
      </div>
    );
  }

  // ======== Render: tablero ========

  const selectedRegion = selectedCell !== null ? puzzle.regions[selectedCell] : null;

  const canUndo = historyIndex > 0;
  const canRedo = historyIndex < history.length - 1;

  return (
    <div className="h-full bg-gradient-to-br from-amber-500 via-orange-500 to-red-600 flex flex-col">
      {isInSession && sessionType && <SessionBanner sessionType={sessionType as 'presencial' | 'remota'} />}

      {/* Barra superior compacta */}
      <div className="bg-black/20 backdrop-blur-sm px-3 py-2 flex items-center gap-2 flex-wrap">
        <span className="text-white font-bold text-base whitespace-nowrap">🍤 Frito Variado</span>
        <div className="w-px h-5 bg-white/30 hidden sm:block" />

        {/* Botones de dificultad */}
        <div className="flex gap-1 flex-wrap">
          {DIFFICULTIES.map(d => (
            <button
              key={d.id}
              onClick={() => navigate(`/juegos/frito-variado/${d.id}`)}
              className={`px-2 py-0.5 rounded text-xs font-semibold transition-all ${difficulty === d.id
                ? 'bg-white text-orange-700'
                : 'bg-white/20 text-white hover:bg-white/30'
                }`}
            >
              {d.icono} {d.nombre}
            </button>
          ))}
        </div>

        <div className="flex-1" />

        {/* Controles */}
        <div className="flex gap-1 items-center">
          {/* Deshacer */}
          <button onClick={undo} disabled={!canUndo} title="Deshacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
          </button>
          {/* Rehacer */}
          <button onClick={redo} disabled={!canRedo} title="Rehacer"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M21 10H11a8 8 0 00-8 8v2M21 10l-6 6m6-6l-6-6" />
            </svg>
          </button>
          {/* Modo asistido */}
          <button onClick={() => setModoAsistido(v => !v)}
            title={modoAsistido ? 'Desactivar modo asistido' : 'Activar modo asistido'}
            className={`p-1.5 rounded transition-all text-white ${modoAsistido ? 'bg-blue-500 ring-2 ring-blue-300' : 'bg-white/20 hover:bg-white/30'}`}>
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/></svg>
          </button>
          {/* Pista */}
          <button onClick={giveHint} disabled={solved} title="Dame una pista"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </button>
          {/* Solución */}
          <button onClick={showSolution} disabled={solved} title="Ver solución"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all disabled:opacity-40">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
          {/* Reiniciar */}
          <button onClick={reset} title="Reiniciar"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
          </button>
          {/* Nuevo tablero */}
          <button onClick={() => generateNewPuzzle(undefined, true)} title="Nuevo tablero"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
            </svg>
          </button>
          {/* Cómo se juega */}
          <button onClick={() => setShowHowToPlay(true)} title="Cómo se juega"
            className="p-1.5 rounded bg-white/20 hover:bg-white/30 text-white transition-all">
            <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      </div>

      {/* Tablero */}
      <div ref={boardAreaRef} className="flex-1 min-h-0 flex items-center justify-center p-1">
        {boardW > 0 && boardH > 0 && (() => {
          const errores = modoAsistido ? calcularErrores(values, puzzle.regions, puzzle.width, puzzle.height) : new Set<number>();
          const cellW = Math.floor(boardW / puzzle.width);
          const cellH = Math.floor(boardH / puzzle.height);
          return (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: `repeat(${puzzle.width}, ${cellW}px)`,
                gridTemplateRows: `repeat(${puzzle.height}, ${cellH}px)`,
                gap: 0,
                backgroundColor: 'rgba(30,41,59,0.6)',
                padding: 3,
                borderRadius: 12,
              }}
            >
              {values.map((value, cellIndex) => {
                const row = Math.floor(cellIndex / puzzle.width);
                const col = cellIndex % puzzle.width;
                const regionId = puzzle.regions[cellIndex];

                const borderTop = row === 0 || puzzle.regions[cellIndex - puzzle.width] !== regionId;
                const borderRight = col === puzzle.width - 1 || puzzle.regions[cellIndex + 1] !== regionId;
                const borderBottom = row === puzzle.height - 1 || puzzle.regions[cellIndex + puzzle.width] !== regionId;
                const borderLeft = col === 0 || puzzle.regions[cellIndex - 1] !== regionId;

                const highlighted = selectedRegion !== null && puzzle.regions[cellIndex] === selectedRegion;
                const regionColor = REGION_PALETTE[regionId % REGION_PALETTE.length];

                return (
                  <FritoCell
                    key={cellIndex}
                    value={value}
                    fixed={fixed[cellIndex]}
                    highlighted={highlighted}
                    regionColor={regionColor}
                    regionIndex={regionId % REGION_PALETTE.length}
                    borderTop={borderTop}
                    borderRight={borderRight}
                    borderBottom={borderBottom}
                    borderLeft={borderLeft}
                    hasError={errores.has(cellIndex)}
                    onClick={() => handleCellClick(cellIndex)}
                  />
                );
              })}
            </div>
          );
        })()}
      </div>

      {/* Modales */}
      <HowToPlayModal
        isOpen={showHowToPlay}
        onClose={() => setShowHowToPlay(false)}
        gameTitle="Frito Variado"
        gameIcon="🍤"
        objective="Rellena el tablero con calamares, boquerones y gambas respetando las reglas de cada región y las fronteras entre regiones."
        rules={[
          'El tablero está dividido en regiones de exactamente 3 celdas (marcadas con bordes gruesos).',
          'Cada región debe tener los 3 fritos IGUALES (3 calamares) O los 3 DIFERENTES (uno de cada tipo).',
          'Dos celdas ortogonalmente adyacentes de regiones distintas NO pueden tener el mismo frito.',
          'Las celdas con triángulo en la esquina son pistas fijas que no se pueden cambiar.',
        ]}
        howToPlay="Haz clic en una celda para ciclar entre vacío → Calamares → Boquerones → Gambas → vacío. Haz clic en una celda para seleccionar y ver resaltada su región."
        author="Neureka"
      />

      <VictoryModal
        isOpen={victoryModalOpen}
        onClose={() => setVictoryModalOpen(false)}
        stats={{ tiempo: elapsedTime, movimientos: moves }}
      />
    </div>
  );
}
